import { useCallback, useEffect, useState } from 'react';
import { cardService } from '@/features/card/services/cardService/cardService';
import type { Card } from '@/features/card/types/card.types';
import { countCardKpis, mapCardKpis, type CardKpiCounts } from '@/features/card/utils/cardKpis.utils';
import { useAsyncGuard } from '@chikara/hooks';

export interface UseCardKpisReturn {
  kpis: CardKpiCounts | null;
  refetch: () => Promise<void>;
}

/**
 * KPIs de resumen de la empresa (BF-630). Independientes de los filtros de la
 * tabla: reflejan el total de tarjetas de la empresa.
 *
 * BF-629: el BE devuelve los agregados en `card-list?kpis=true`; se piden en una
 * sola llamada. Si el BE aún no los expone, se cae al conteo local para no
 * romper un despliegue adelantado del FE.
 */
export const useCardKpis = (enabled: boolean = true): UseCardKpisReturn => {
  const [kpis, setKpis] = useState<CardKpiCounts | null>(null);
  const { checkGuard } = useAsyncGuard();

  const fetchKpis = useCallback(async () => {
    if (!enabled || !checkGuard()) return;

    try {
      const summary = await cardService.listCards({ page: 1, limit: 1, kpis: true });
      if (summary?.kpis) {
        setKpis(mapCardKpis(summary.kpis));
        return;
      }

      // Fallback: BE sin `kpis` (despliegue adelantado del FE).
      const meta = await cardService.listCards({ page: 1, limit: 1, balance: true });
      const total: number = meta?.pagination?.total_items ?? 0;

      const response =
        total > 1 ? await cardService.listCards({ page: 1, limit: total, balance: true }) : meta;

      const cards = (response?.data ?? [])
        .filter((item: any) => item.status !== 'pending_approval' && item.status !== 'pending')
        .map((item: any) => ({
          ...item,
          creditLimit: item.balance?.assigned_limit ?? 0,
        }));
      setKpis(countCardKpis(cards as Card[]));
    } catch (err) {
      console.error(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (enabled) fetchKpis();
  }, [enabled, fetchKpis]);

  return { kpis, refetch: fetchKpis };
};
